/**
 * AboutToFinishBoard — watchlist card grid for real-time capture mode.
 *
 * React.memo + React.forwardRef:
 *  - Memo prevents re-renders when only unrelated parent state changes
 *  - forwardRef + useImperativeHandle exposes addBlankBox() to parent
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, TextField } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { MAX_WATCHLIST_SLOTS, nextWatchOrder, sailMatches } from "./constants";

// ─── Memoized sail input ─────────────────────────────────────────────────────
// A keystroke re-renders ONLY the box being typed (other boxes keep identical
// props). Uppercase is applied via CSS + at capture time — rewriting the
// controlled value per keystroke broke soft-keyboard composition on tablets
// (the keyboard restarted composition after every transformed character →
// "very laggy typing"). Autocorrect/suggestions are disabled for the same
// reason.
const BlankSailInput = React.memo(function BlankSailInput({
  id,
  value,
  autoFocus,
  compact768,
  compactTablet,
  onChangeValue,
  onEnter,
  registerInput,
  ord,
}) {
  return (
    <TextField
      value={value}
      // Cap at 8 in the controlled value too — maxLength alone is ignored by
      // tablet IMEs mid-composition (they typed past 8, then trimmed on blur).
      onChange={(e) => onChangeValue(id, e.target.value.slice(0, 8))}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onEnter(id);
        }
        boardTabKeyDown(e);
      }}
      placeholder="Sail#"
      size="small"
      variant="standard"
      inputRef={(el) => registerInput(id, el)}
      InputProps={{ disableUnderline: true }}
      inputProps={{
        maxLength: 8,
        autoComplete: "off",
        autoCorrect: "off",
        autoCapitalize: "characters",
        spellCheck: "false",
        enterKeyHint: "done",
        "data-atf-ord": ord,
      }}
      autoFocus={autoFocus}
      sx={{
        width: "100%", minWidth: 0, touchAction: "manipulation",
        "& input": {
          textAlign: "center", fontWeight: 700, fontFamily: "monospace",
          textTransform: "uppercase",
          fontSize: compact768 ? 10 : compactTablet ? 11 : 13,
          p: "2px 2px", bgcolor: "#fff", borderRadius: 2, border: "1px solid #ddd",
          height: 24, boxSizing: "border-box",
        },
      }}
    />
  );
});

// Monotonic unique id for blank boxes. `Date.now()` can collide when two boxes
// are created in the same millisecond (rapid + / add) → duplicate React keys →
// React drops one and the far-left box vanishes. A counter guarantees uniqueness.
let _blankSeq = 0;
const newBlankId = () => `blank-${++_blankSeq}`;

// A box is "filled" (claims its slot) when it has a sail — or, in place mode,
// a Pos value (typing either one claims the slot).
const boxFilled = (b, isPlace) =>
  (b.sailNumber || "").trim() !== "" ||
  (!!isPlace && String(b.pos ?? "").trim() !== "");

// Slot stamp: cards render at their stamp position (positional model — boxes
// do NOT move when filled). Unstamped boxes (fresh auto-created leads) sort
// smallest so they naturally take the far-left lead slot.
const boxStamp = (b) => {
  const n = Number(b?._watchOrder);
  return Number.isFinite(n) ? n : -1;
};

// THE DEFAULT BOX ("Box 1") IS THE UNSTAMPED ONE — load-bearing rule.
// Every auto-created pinned lead ("blank-default", withLeadEmpty, the slot-cap
// effect, applyFillRules' fresh lead) is created WITHOUT `_watchOrder`; the
// swap in applyFillRules also hands its undefined stamp to the box it swaps
// with, so that box becomes the new default. Boxes that OWN a slot — "+"-added
// empties and boxes the user cleared — always carry a real stamp.
// Only the default box relocates its text (see applyFillRules); every other
// box fills IN PLACE. Selecting the default by "lowest stamp" instead of
// "unstamped" promoted a user-cleared box to the far-left whenever it was the
// only empty (the "cleared box jumps to Box 1" bug) — do not go back to that.
// A box the user CLEARED is never the default, even if it happens to be
// unstamped: when the board is full, typing in Box 1 leaves the text in Box 1
// (spec rule 7) and that box stays UNSTAMPED while filled. Clearing it used to
// hand it back to the default logic → it jumped left and relocated its text on
// the next keystroke (the "2nd box becomes 1st" bug).
const isDefaultBox = (b) => boxStamp(b) < 0 && !b?.userCleared;

// Slot stamp given to a cleared box that had none, so it OWNS its slot instead
// of falling back to the unstamped (-1) default sentinel. Real stamps come
// from nextWatchOrder() (Date.now()-based, ~1.7e12) so this sorts before every
// stamped box — keeping the cleared box exactly where it already was — while
// still sorting after preFinish reload entries (index fallback 0..5).
const CLEARED_SLOT_STAMP = 100;

const entryKeyOf = (e) => `entry-${e.id || e.sailNum}`;

// ── Visual-order Tab chain ───────────────────────────────────────────────────
// The board's DOM order is stable/append-only (CSS `order` does the layout),
// so native Tab follows CREATION order — visually wrong (empty→empty,
// typed→typed). Every board input carries data-atf-ord = cardVisualOrder*10 +
// fieldIndex; Tab hops to the numerically next/previous input.
const focusAdjacentBoardField = (currentInput, backwards) => {
  const board = currentInput.closest("[data-atf-board]");
  if (!board) return false;
  // ANY element carrying data-atf-ord participates: inputs AND the entry
  // cards' green ✓ buttons (time-mode entries have no input — enumerating
  // inputs only made Tab skip them).
  const fields = Array.from(board.querySelectorAll("[data-atf-ord]")).sort(
    (a, b) => Number(a.dataset.atfOrd) - Number(b.dataset.atfOrd),
  );
  const i = fields.indexOf(currentInput);
  if (i === -1) return false;
  const next = fields[i + (backwards ? -1 : 1)];
  if (!next) return false;
  next.focus();
  next.select?.();
  return true;
};

// Shared Tab keydown for board inputs (falls through to native Tab at the
// chain's ends so focus can leave the board).
const boardTabKeyDown = (e) => {
  if (e.key !== "Tab") return;
  if (focusAdjacentBoardField(e.target, e.shiftKey)) {
    e.preventDefault();
    e.stopPropagation();
  }
};

const AboutToFinishBoard = React.memo(React.forwardRef(({
  entries,
  snWithAlpha,
  isPlace,
  onRemove,
  onCaptureNow,
  onCaptureTyped,
  onEntryPlaceChange,
  onCountChange,
}, ref) => {
  const compactTablet = useMediaQuery("(max-width:1024px)");
  const compact768 = useMediaQuery("(max-width:768px)");
  const [blankBoxes, setBlankBoxes] = useState([{ id: "blank-default", sailNumber: "" }]);
  const focusBlankIdRef = useRef(null);
  const blankInputRefs = useRef({});

  // ── Move-in consumption of spare empties ──────────────────────────────────
  // A moved-in entry "consumes" a spare empty box. The pairing is DERIVED at
  // render time (the spare is hidden the SAME frame the entry appears — no
  // setState during render, no glitch, no queued-update clobbering) and then
  // COMMITTED by the pairing effect after the render (override persisted,
  // spare pruned from state — invisible, it was already hidden). See the
  // "Derived entry↔spare pairing" block in the render section.
  const entryOrderOverrides = useRef(new Map());

  React.useImperativeHandle(ref, () => ({
    addBlankBox: () => {
      if (entries.length + blankBoxes.length >= MAX_WATCHLIST_SLOTS) return;
      // Stamp at creation so "+"-added empties keep their insertion order alongside
      // moved-in entries and typed boxes (a later entry sorts AFTER them).
      setBlankBoxes((prev) => [
        ...prev,
        { id: newBlankId(), sailNumber: "", _watchOrder: nextWatchOrder() },
      ]);
    },
    getFilledBlankCount: () =>
      blankBoxes.filter((b) => boxFilled(b, isPlace)).length,
    clearEmptyBoxes: () => {
      setBlankBoxes((prev) => {
        const filled = prev.filter((b) => boxFilled(b, isPlace));
        return filled.length > 0 ? filled : [];
      });
    },
    hasSailNumber: (sailNum) => {
      const lower = (sailNum || "").trim().toLowerCase();
      return blankBoxes.some((b) => b.sailNumber.trim().toLowerCase() === lower);
    },
    // A finished-grid sail correction now owns this typed sail → remove the
    // matching TYPED box from the board (the LAST-added one when several share
    // the sail — highest slot stamp, later array index on ties). Returns
    // whether a box was consumed.
    consumeTypedSail: (sailNum, alpha) => {
      const matches = blankBoxes.filter(
        (b) =>
          b.sailNumber.trim() && sailMatches(b.sailNumber, sailNum, alpha),
      );
      if (matches.length === 0) return false;
      const last = matches.reduce((a, b) =>
        boxStamp(b) >= boxStamp(a) ? b : a,
      );
      handleRemoveBlankBox(last.id);
      return true;
    },
  }), [entries.length, blankBoxes.length, blankBoxes]);

  // Report total count to parent — capped at MAX (mirrors the render cap) so the
  // badge never flashes 7/6 in the frame between a move and the slot useEffect
  // trimming the leftover empty from state.
  const totalCount = Math.min(MAX_WATCHLIST_SLOTS, entries.length + blankBoxes.length);
  const prevCountRef = useRef(-1);
  if (prevCountRef.current !== totalCount) {
    prevCountRef.current = totalCount;
    Promise.resolve().then(() => onCountChange?.(totalCount));
  }

  // After a typed box repositions (first character stamps it → it re-sorts to
  // the END while a fresh lead is inserted), some browsers — iOS Safari and
  // Android WebView notably — BLUR an input whose DOM node is moved. React
  // can't restore that focus (the node never remounts, so autoFocus won't
  // re-fire) → the following keystrokes were silently dropped until the user
  // tapped the box again ("laggy typing", "typing doesn't follow the box").
  // One-shot imperative refocus with the caret at the end fixes it.
  useEffect(() => {
    const idToFocus = focusBlankIdRef.current;
    if (!idToFocus) return;
    focusBlankIdRef.current = null; // one-shot
    const el = blankInputRefs.current[idToFocus];
    if (el && document.activeElement !== el) {
      el.focus();
      const len = el.value?.length ?? 0;
      try {
        el.setSelectionRange(len, len);
      } catch {
        /* input types without selection support */
      }
    }
  }, [blankBoxes]);

  // Refs mirroring props/state for the stable []-dep handlers below (same
  // pattern as FinishTimeBody) — stable handlers let each BlankSailInput be
  // memoized, so a keystroke re-renders only the box being typed.
  const blankBoxesRef = useRef(blankBoxes);
  blankBoxesRef.current = blankBoxes;
  const entriesLenRef = useRef(entries.length);
  entriesLenRef.current = entries.length;
  const onCaptureTypedRef = useRef(onCaptureTyped);
  onCaptureTypedRef.current = onCaptureTyped;
  const isPlaceRef = useRef(isPlace);
  isPlaceRef.current = isPlace;

  const registerInput = useCallback((id, el) => {
    blankInputRefs.current[id] = el;
  }, []);

  // Positional fill rules, shared by the sail and Pos inputs:
  // - a NON-lead box fills IN PLACE (keeps its slot stamp — the box the user
  //   typed in is exactly where the card stays);
  // - the LEAD box swaps slot stamps with the next-lowest empty, so the typed
  //   card lands in the first available slot and an empty stays far-left;
  // - lead with NO other empty: the typed card moves after the filled cards
  //   (fresh stamp) and a new unstamped lead is prepended (when room).
  const applyFillRules = useCallback((prev, next, box, id) => {
    const otherEmpties = prev
      .filter((b) => b.id !== id && !boxFilled(b, isPlaceRef.current))
      .sort((a, b) => boxStamp(a) - boxStamp(b));
    // ONLY the default box (unstamped, far-left "Box 1") relocates its text to
    // the next available slot. A box that owns a slot — "+"-added or one the
    // user cleared — always fills IN PLACE, so it can never jump to another
    // slot when typed into again.
    const isLead = isDefaultBox(box);
    if (!isLead) return next; // fills in place
    const nextEmpty = otherEmpties[0];
    if (nextEmpty) {
      const myStamp = box._watchOrder; // undefined → the target becomes the new default
      const theirStamp = nextEmpty._watchOrder;
      return next.map((b) =>
        b.id === id
          ? { ...b, _watchOrder: theirStamp }
          : b.id === nextEmpty.id
            ? {
                ...b,
                _watchOrder: myStamp,
                // It IS the pinned default now — drop cleared-box protection.
                userCleared: false,
                _clearedStamp: undefined,
              }
            : b,
      );
    }
    // Lead is the ONLY empty. With room: the typed card moves after the
    // filled cards and a fresh lead appears. Board FULL: the text simply
    // stays in Box 1 (no relocation — there is nowhere to move).
    if (entriesLenRef.current + next.length < MAX_WATCHLIST_SLOTS) {
      const out = next.map((b) =>
        b.id === id ? { ...b, _watchOrder: nextWatchOrder() } : b,
      );
      return [{ id: newBlankId(), sailNumber: "" }, ...out];
    }
    return next;
  }, []);

  const handleBlankChange = useCallback((id, val) => {
    // Value is stored AS TYPED — uppercase happens via CSS and at capture
    // (see BlankSailInput). Matching is case-insensitive everywhere.
    setBlankBoxes((prev) => {
      let next = prev.map((b) => (b.id === id ? { ...b, sailNumber: val } : b));
      const box = prev.find((b) => b.id === id);
      const justFilled =
        box &&
        !boxFilled(box, isPlaceRef.current) &&
        boxFilled({ ...box, sailNumber: val }, isPlaceRef.current);
      if (justFilled) {
        next = applyFillRules(prev, next, box, id);
        // Box is now filled — clear the cleared-box protection.
        next = next.map((b) => (b.id === id ? { ...b, userCleared: false, _clearedStamp: undefined } : b));
        focusBlankIdRef.current = id; // focus safety net (CSS order keeps focus anyway)
      }
      // When a filled box is cleared back to empty:
      // - Keep _watchOrder intact so the box stays in its visual slot.
      // - Set userCleared=true + _clearedStamp so the slot-trim treats it as
      //   high-priority AND only entries added AFTER this point (stamp >
      //   _clearedStamp) are allowed to claim it. Entries that were already
      //   waiting (stamp <= _clearedStamp) cannot consume it, preventing the
      //   box from vanishing the moment the user clears the sail.
      const justCleared =
        box &&
        boxFilled(box, isPlaceRef.current) &&
        !boxFilled({ ...box, sailNumber: val }, isPlaceRef.current);
      if (justCleared) {
        next = next.map((b) =>
          b.id === id
            ? {
                ...b,
                // An unstamped box (filled in place at Box 1 when the board was
                // full) must ACQUIRE a slot stamp on clear — otherwise it falls
                // back to the -1 default sentinel and jumps to the far left.
                _watchOrder: b._watchOrder ?? CLEARED_SLOT_STAMP,
                userCleared: true,
                _clearedStamp: nextWatchOrder(),
              }
            : b,
        );
      }
      return next;
    });
  }, [applyFillRules]);

  // Removing/capturing a typed box doesn't change `entries`, so the slot
  // useEffect won't run — ensure the pinned lead empty reappears here whenever a
  // slot opens up (otherwise the far-left input stops showing after going full).
  const withLeadEmpty = useCallback((boxes) => {
    // Must be a DEFAULT box (unstamped) — a user-cleared or "+" empty owns its
    // own slot and is NOT the far-left default, so its presence must not
    // suppress re-adding the default. This is what makes the default reappear
    // after a × delete frees a slot while a cleared box sits on the board.
    const hasDefault = boxes.some(
      (b) => !boxFilled(b, isPlaceRef.current) && isDefaultBox(b),
    );
    // Room check counts TOTAL boxes (cleared/"+" empties included) so adding
    // the default can never push the board past MAX slots.
    return !hasDefault &&
      entriesLenRef.current + boxes.length < MAX_WATCHLIST_SLOTS
      ? [{ id: newBlankId(), sailNumber: "" }, ...boxes]
      : boxes;
  }, []);

  // Place mode: a typed box also carries a Pos value (kept as typed; capture
  // consumes it). Typing a Pos FILLS the box under the same positional rules
  // as typing a sail. (No focus ref here: focus sits in the Pos input, which
  // never moves.)
  const handleBlankPosChange = useCallback((id, pos) => {
    setBlankBoxes((prev) => {
      let next = prev.map((b) => (b.id === id ? { ...b, pos } : b));
      const box = prev.find((b) => b.id === id);
      const justFilled =
        box &&
        !boxFilled(box, isPlaceRef.current) &&
        boxFilled({ ...box, pos }, isPlaceRef.current);
      if (justFilled) {
        next = applyFillRules(prev, next, box, id);
        // Box is now filled — clear the cleared-box protection.
        next = next.map((b) => (b.id === id ? { ...b, userCleared: false, _clearedStamp: undefined } : b));
      }
      // Mirror of handleBlankChange: clearing pos (in place mode) can make
      // the box fully empty — protect it the same way.
      const justCleared =
        box &&
        boxFilled(box, isPlaceRef.current) &&
        !boxFilled({ ...box, pos }, isPlaceRef.current);
      if (justCleared) {
        next = next.map((b) =>
          b.id === id
            ? {
                ...b,
                // An unstamped box (filled in place at Box 1 when the board was
                // full) must ACQUIRE a slot stamp on clear — otherwise it falls
                // back to the -1 default sentinel and jumps to the far left.
                _watchOrder: b._watchOrder ?? CLEARED_SLOT_STAMP,
                userCleared: true,
                _clearedStamp: nextWatchOrder(),
              }
            : b,
        );
      }
      return next;
    });
  }, [applyFillRules]);

  const handleBlankCapture = useCallback(
    (id) => {
      const box = blankBoxesRef.current.find((b) => b.id === id);
      if (!box || !box.sailNumber.trim()) return;
      const success = onCaptureTypedRef.current?.(
        box.sailNumber.trim(),
        box.pos,
      );
      if (!success) return;
      setBlankBoxes((prev) => withLeadEmpty(prev.filter((b) => b.id !== id)));
    },
    [withLeadEmpty],
  );

  const handleRemoveBlankBox = useCallback(
    (id) => {
      setBlankBoxes((prev) => withLeadEmpty(prev.filter((b) => b.id !== id)));
    },
    [withLeadEmpty],
  );

  // ── Render ────────────────────────────────────────────────────────────────
  // POSITIONAL ORDER MODEL: every card sits at its slot stamp; boxes do NOT
  // move when filled (only the lead's fill swaps slots — see applyFillRules).
  // Visual order = [the lead: lowest-stamp EMPTY box, pinned first] then
  // everything else (entries at their override/own stamp, boxes at theirs)
  // ascending. A cleared box stays at its slot as an empty (the natural
  // "next available" for the following lead-fill or move-in).
  const isFilledBox = (b) => boxFilled(b, isPlace);
  const emptiesSorted = blankBoxes
    .filter((b) => !isFilledBox(b))
    .sort((a, b) => boxStamp(a) - boxStamp(b));
  const filledBoxCountEarly = blankBoxes.length - emptiesSorted.length;
  // Render the lead only when a slot exists for it — a move-in that had no
  // spare empty to consume otherwise showed 7 cards for a frame until the
  // slot effect trimmed state (the "7th box" flash).
  // The default box is the UNSTAMPED empty (see isDefaultBox) — NOT merely the
  // lowest-stamp one. A user-cleared box keeps its own stamp/slot, so when it
  // is the only empty (6/6 typed) there is simply no far-left default until a
  // delete frees a slot.
  const leadEmpty =
    entries.length + filledBoxCountEarly < MAX_WATCHLIST_SLOTS
      ? emptiesSorted.find(isDefaultBox)
      : undefined; // pinned far-left input
  // ── Derived entry↔spare pairing ────────────────────────────────────────────
  // Each not-yet-paired entry (own stamp = arrived via chip) claims the
  // lowest-stamp spare empty that existed BEFORE it: the entry renders at
  // that spare's slot and the spare is hidden THIS frame. Pure derivation —
  // the commit effect below persists the override and prunes the spare.
  const overrides = entryOrderOverrides.current;
  // Every empty EXCEPT the default, stamp-ascending. Must exclude by id, not
  // slice(1): the default is no longer guaranteed to be index 0 (a cleared box
  // can have a lower stamp), and slicing would drop that box out of `spares`
  // → out of shownEmptyIds → it would not render at all (box vanishes).
  const spares = emptiesSorted.filter((b) => b.id !== leadEmpty?.id);
  const hiddenSpareIds = new Set();
  const pendingPairs = [];
  for (const e of entries) {
    const key = entryKeyOf(e);
    if (overrides.has(key)) continue;
    const own = Number(e._watchOrder);
    if (!Number.isFinite(own)) continue; // preFinish reload entries: index order
    const spare = spares.find(
      (s) =>
        !hiddenSpareIds.has(s.id) &&
        (!s.userCleared || own > s._clearedStamp) &&
        boxStamp(s) < own,
    );
    if (!spare) continue;
    hiddenSpareIds.add(spare.id);
    pendingPairs.push({ key, spareId: spare.id, stamp: boxStamp(spare) });
  }
  const pendingByKey = new Map(pendingPairs.map((p) => [p.key, p.stamp]));

  // Cap the EXTRA empties shown to the free slots (drop highest slots first)
  // so the board never shows > MAX. Hidden (paired) spares never render.
  const filledBoxCount = filledBoxCountEarly;
  const room = Math.max(
    0,
    MAX_WATCHLIST_SLOTS - entries.length - filledBoxCount - (leadEmpty ? 1 : 0),
  );
  const shownEmptyIds = new Set(
    spares
      .filter((s) => !hiddenSpareIds.has(s.id))
      .slice(0, room)
      .map((b) => b.id),
  );

  // preFinish reload entries have no stamp/override → index fallback (small)
  // keeps them right after the lead, before any newly added card.
  const rest = [
    ...entries.map((e, i) => ({
      kind: "entry",
      key: entryKeyOf(e),
      order:
        overrides.get(entryKeyOf(e)) ??
        pendingByKey.get(entryKeyOf(e)) ??
        (Number.isFinite(Number(e._watchOrder)) ? Number(e._watchOrder) : i),
      data: e,
    })),
    ...blankBoxes
      .filter(
        (b) =>
          b.id !== leadEmpty?.id &&
          (isFilledBox(b) || shownEmptyIds.has(b.id)),
      )
      .map((b) => ({ kind: "blank", key: b.id, order: boxStamp(b), data: b })),
  ].sort((a, b) => a.order - b.order);

  // Commit the derived pairing after paint: persist overrides, prune the
  // hidden spares from state (invisible — they were already hidden this
  // frame), and drop overrides for entries that left the board (so a
  // re-added sail pairs fresh). Functional updater — composes with any
  // queued typing updates.
  useEffect(() => {
    const valid = new Set(entries.map(entryKeyOf));
    for (const k of Array.from(entryOrderOverrides.current.keys())) {
      if (!valid.has(k)) entryOrderOverrides.current.delete(k);
    }
    if (pendingPairs.length === 0) return;
    for (const p of pendingPairs) {
      entryOrderOverrides.current.set(p.key, p.stamp);
    }
    const ids = new Set(pendingPairs.map((p) => p.spareId));
    setBlankBoxes((prev) => prev.filter((b) => !ids.has(b.id)));
  });

  // Keep blank boxes within slot limits AND guarantee one pinned empty input
  // whenever there's a free slot. Idempotent (only changes state when needed),
  // so it can safely depend on blankBoxes without looping.
  // ORDERING: declared AFTER the pairing-commit effect above — on a move-in both
  // run in the same commit, and the consumed spare must be PRUNED before this
  // cap trim runs. Declared earlier, the trim counted the (hidden, about-to-be-
  // pruned) spare against the budget and deleted an extra right-most "+"-empty
  // (the "6th box vanishes on move-in" bug).
  useEffect(() => {
    setBlankBoxes((prev) => {
      const maxBlankSlots = Math.max(0, MAX_WATCHLIST_SLOTS - entries.length);
      let next = prev;
      // Over capacity → keep FILLED boxes (sail or place-mode Pos) first,
      // then the LOWEST-slot empties (lead first).
      if (next.length > maxBlankSlots) {
        // userCleared boxes are given filled-box priority so they are not
        // dropped when a new entry arrives and shrinks the blank-slot budget.
        const filled = next.filter((b) => boxFilled(b, isPlaceRef.current) || b.userCleared);
        const empty = next
          .filter((b) => !boxFilled(b, isPlaceRef.current) && !b.userCleared)
          .sort((a, b) => boxStamp(a) - boxStamp(b));
        const keepFilled = filled.slice(0, maxBlankSlots);
        const keepEmpty = empty.slice(0, Math.max(0, maxBlankSlots - keepFilled.length));
        next = next.filter((b) => keepFilled.includes(b) || keepEmpty.includes(b));
      }
      // Free slot but no DEFAULT box → add a pinned lead at the front. Same
      // rule as withLeadEmpty: cleared / "+" empties own their slots and do
      // not count as the far-left default.
      const hasDefault = next.some(
        (b) => !boxFilled(b, isPlaceRef.current) && isDefaultBox(b),
      );
      if (!hasDefault && entries.length + next.length < MAX_WATCHLIST_SLOTS) {
        next = [{ id: newBlankId(), sailNumber: "" }, ...next];
      }
      return next;
    });
  }, [entries.length]);

  const renderBlankCard = (box, onCross, order) => {
    const hasContent = !!box.sailNumber.trim();
    return (
      <Box key={box.id} sx={{
        order,
        display: "grid", gridTemplateColumns: "auto minmax(0,1fr) auto", alignItems: "center", gap: 0.5,
        bgcolor: "#fff8e1", border: "1px solid #f0a500", borderRadius: 2, p: 0.75,
      }}>
        <CircleButton
          color="#e53935" hoverColor="#b71c1c"
          onClick={onCross}
          title="Remove"
          dataOrd={order * 10}
        ><CloseIcon sx={{ fontSize: 14 }} /></CircleButton>
        <BlankSailInput
          id={box.id}
          value={box.sailNumber}
          autoFocus={focusBlankIdRef.current === box.id}
          compact768={compact768}
          compactTablet={compactTablet}
          onChangeValue={handleBlankChange}
          onEnter={handleBlankCapture}
          registerInput={registerInput}
          ord={order * 10 + 1}
        />
        {hasContent ? (
          <CircleButton
            color="#43a047" hoverColor="#2e7d32"
            onClick={() => handleBlankCapture(box.id)}
            title="Capture time now"
            dataOrd={order * 10 + 3}
          ><CheckIcon sx={{ fontSize: 14 }} /></CircleButton>
        ) : (
          <Box sx={{ width: 20, height: 20, flexShrink: 0 }} aria-hidden />
        )}
      </Box>
    );
  };

  const renderEntryCard = (entry, order) => {
    const sail = (entry.sailNum || "").toUpperCase();
    return (
      <Box key={entry.id || entry.sailNum} sx={{
        order,
        display: "grid", gridTemplateColumns: "auto minmax(0,1fr) auto", alignItems: "center", gap: 0.5,
        bgcolor: "#fff8e1", border: "1px solid #f0a500", borderRadius: 2, p: 0.75,
      }}>
        <CircleButton color="#e53935" hoverColor="#b71c1c" onClick={() => onRemove(entry)} title="Remove" dataOrd={order * 10}><CloseIcon sx={{ fontSize: 14 }} /></CircleButton>
        <SailLabel compact768={compact768} compactTablet={compactTablet} title={sail}>{sail}</SailLabel>
        <CircleButton color="#43a047" hoverColor="#2e7d32" onClick={() => onCaptureNow(entry)} title="Finish (capture time now)" dataOrd={order * 10 + 2}><CheckIcon sx={{ fontSize: 14 }} /></CircleButton>
      </Box>
    );
  };

  // ── PLACE-MODE card variants (isolation contract, TESTING.md §0) ──────────
  // Separate components so place styling/structure can never leak into the
  // time-mode cards above. Layout: sail input dominates (1fr), Pos is a fixed
  // ~3-digit box.
  const renderPlaceBlankCard = (box, onCross, order) => {
    const hasContent = !!box.sailNumber.trim();
    return (
      <Box key={box.id} sx={{
        order,
        display: "grid",
        // Sail = wide flexible (1fr); Pos = FIXED narrow track. A fixed px track
        // can't grow to the input's intrinsic (size=20 ~145px) width, so Pos
        // stays small and Sail keeps the space. Place mode only.
        gridTemplateColumns: `auto minmax(0,1fr) ${compact768 ? 30 : compactTablet ? 34 : 38}px auto`,
        alignItems: "center", gap: 0.5,
        bgcolor: "#fff8e1", border: "1px solid #f0a500", borderRadius: 2, p: 0.75,
      }}>
        <CircleButton
          color="#e53935" hoverColor="#b71c1c"
          onClick={onCross}
          title="Remove"
          dataOrd={order * 10}
        ><CloseIcon sx={{ fontSize: 14 }} /></CircleButton>
        <BlankSailInput
          id={box.id}
          value={box.sailNumber}
          autoFocus={focusBlankIdRef.current === box.id}
          compact768={compact768}
          compactTablet={compactTablet}
          onChangeValue={handleBlankChange}
          onEnter={handleBlankCapture}
          registerInput={registerInput}
          ord={order * 10 + 1}
        />
        <BlankPosInput
          id={box.id}
          value={box.pos}
          compact768={compact768}
          compactTablet={compactTablet}
          onChangeValue={handleBlankPosChange}
          onEnter={handleBlankCapture}
          ord={order * 10 + 2}
        />
        {hasContent ? (
          <CircleButton
            color="#43a047" hoverColor="#2e7d32"
            onClick={() => handleBlankCapture(box.id)}
            title="Finish (save place)"
            dataOrd={order * 10 + 3}
          ><CheckIcon sx={{ fontSize: 14 }} /></CircleButton>
        ) : (
          <Box sx={{ width: 20, height: 20, flexShrink: 0 }} aria-hidden />
        )}
      </Box>
    );
  };

  const renderPlaceEntryCard = (entry, order) => {
    const sail = (entry.sailNum || "").toUpperCase();
    return (
      <Box key={entry.id || entry.sailNum} sx={{
        order,
        display: "grid",
        // Sail = wide flexible (1fr); Pos = FIXED narrow track (see blank card).
        gridTemplateColumns: `auto minmax(0,1fr) ${compact768 ? 30 : compactTablet ? 34 : 38}px auto`,
        alignItems: "center", gap: 0.5,
        bgcolor: "#fff8e1", border: "1px solid #f0a500", borderRadius: 2, p: 0.75,
      }}>
        <CircleButton color="#e53935" hoverColor="#b71c1c" onClick={() => onRemove(entry)} title="Remove" dataOrd={order * 10}><CloseIcon sx={{ fontSize: 14 }} /></CircleButton>
        <SailLabel compact768={compact768} compactTablet={compactTablet} title={sail}>{sail}</SailLabel>
        <CardPosInput
          entry={entry}
          compact768={compact768}
          compactTablet={compactTablet}
          onPlaceChange={onEntryPlaceChange}
          ord={order * 10 + 1}
        />
        <CircleButton
          color="#43a047"
          hoverColor="#2e7d32"
          onClick={() => onCaptureNow(entry)}
          title="Finish (save place)"
          dataOrd={order * 10 + 2}
        ><CheckIcon sx={{ fontSize: 14 }} /></CircleButton>
      </Box>
    );
  };

  // VISUAL order (what the user sees): lead empty first, then everything at
  // its slot stamp (positional model — see the Render comment above).
  const orderedCards = [
    ...(leadEmpty ? [{ kind: "blank", key: leadEmpty.id, data: leadEmpty }] : []),
    ...rest,
  ];

  // DOM order is kept STABLE (append-only) and the visual arrangement is done
  // purely with CSS `order` on each card. Reordering React children physically
  // MOVED the focused input's DOM node on the first keystroke (box jumps
  // right) — browsers blur a moved focused element and the move+new-lead mount
  // landed inside the keystroke = the typing lag/hitch. With CSS order the
  // node (and focus/caret/keyboard) never moves; the jump is pure paint.
  const orderOf = new Map(orderedCards.map((c, i) => [c.key, i]));
  const entryRankOf = new Map(
    entries.map((e, i) => [`entry-${e.id || e.sailNum}`, i]),
  );
  const blankSeq = (id) => {
    const n = parseInt(String(id).replace("blank-", ""), 10);
    return Number.isFinite(n) ? n : 0; // "blank-default" → 0
  };
  const stableRank = (card) =>
    card.kind === "entry"
      ? entryRankOf.get(card.key) ?? 0
      : 100000 + blankSeq(card.data.id);
  const domCards = [...orderedCards].sort(
    (a, b) => stableRank(a) - stableRank(b),
  );
  const visualLead = orderedCards[0];

  // × on the left-most box when it's EMPTY removes the RIGHT-most box (peels
  // 6th → 5th → 4th …). Never the left-most box itself; a moved-in entry returns
  // to Unfinished via onRemove. (Uses the VISUAL order.)
  const removeRightMostBox = () => {
    for (let i = orderedCards.length - 1; i >= 1; i--) {
      const c = orderedCards[i];
      if (c.kind === "entry") { onRemove(c.data); return; }
      handleRemoveBlankBox(c.data.id);
      return;
    }
  };

  return (
    <Box>
      <Box
        data-atf-board="true"
        sx={{
          display: "grid",
          // Cards per row: 3 on small tablet/phone (≤768). Big tablet
          // (768–1024) keeps 6 in time mode, but PLACE cards carry an extra
          // Pos box and get cramped, so they drop to 3 per row there too.
          // Desktop (>1024) is always 6.
          gridTemplateColumns: compact768
            ? "repeat(3, 1fr)"
            : compactTablet && isPlace
              ? "repeat(3, 1fr)"
              : "repeat(6, 1fr)",
          gap: compact768 ? 0.75 : 1,
        }}
      >
        {domCards.map((card) => {
          const order = orderOf.get(card.key) ?? 0;
          if (card.kind === "entry") {
            return isPlace
              ? renderPlaceEntryCard(card.data, order)
              : renderEntryCard(card.data, order);
          }
          // VISUALLY left-most box empty → remove right-most box; else remove self.
          const onCross =
            visualLead?.key === card.key && !card.data.sailNumber.trim()
              ? removeRightMostBox
              : () => handleRemoveBlankBox(card.data.id);
          return isPlace
            ? renderPlaceBlankCard(card.data, onCross, order)
            : renderBlankCard(card.data, onCross, order);
        })}
      </Box>
    </Box>
  );
}));

export default AboutToFinishBoard;

// ─── Internal sub-components ────────────────────────────────────────────────

function CircleButton({ color, hoverColor, onClick, title, children, dataOrd }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      // Optional slot in the board's visual-order Tab chain (entry cards'
      // green ✓ — they have no input in time mode).
      data-atf-ord={dataOrd}
      onKeyDown={dataOrd != null ? boardTabKeyDown : undefined}
      sx={{
        position: "relative",
        width: 20, height: 20, borderRadius: "50%", bgcolor: color, color: "#fff",
        display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        p: 0, m: 0, border: 0, lineHeight: 1, cursor: "pointer", appearance: "none",
        userSelect: "none", touchAction: "manipulation", WebkitTapHighlightColor: "transparent",
        // Invisible enlarged touch target (does not affect layout)
        "&::after": { content: '""', position: "absolute", inset: "-9px", borderRadius: "50%" },
        "@media (hover: hover)": { "&:hover": { bgcolor: hoverColor } },
        "&:active": { bgcolor: hoverColor },
        "&:focus-visible": { outline: "2px solid #1976d2", outlineOffset: 1 },
      }}
    >
      {children}
    </Box>
  );
}

/** Centered sail number label */
function SailLabel({ compact768, compactTablet, title, children }) {
  return (
    <Box sx={{
      fontWeight: 700, fontFamily: "monospace",
      fontSize: compact768 ? 10 : compactTablet ? 11 : 13,
      textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
    }} title={title}>
      {children}
    </Box>
  );
}

// ─── Place-mode Pos input on moved-in entry cards ───────────────────────────
// Commits on blur/Enter → onPlaceChange (Redux + prefinish persistence in
// FinishTimeBody.handleAboutPlaceChange). Rendered only when isPlace.

const CardPosInput = React.memo(function CardPosInput({
  entry, compact768, compactTablet, onPlaceChange, ord,
}) {
  const initial =
    entry.place === 0 || entry.place == null ? "" : String(entry.place);
  const [val, setVal] = useState(initial);
  // Sync external changes (safe — commits only happen on blur/Enter, so this
  // never fights active typing).
  useEffect(() => { setVal(initial); }, [initial]);
  const commit = () => {
    const n = parseInt(val, 10);
    const place = isNaN(n) || n <= 0 ? "" : n;
    setVal(place === "" ? "" : String(place));
    const current =
      entry.place === 0 || entry.place == null ? "" : Number(entry.place);
    if (place !== current) onPlaceChange(entry.id, place);
  };
  return (
    <TextField
      value={val}
      placeholder="PLC"
      size="small"
      variant="standard"
      onChange={(e) => {
        if (/^\d*$/.test(e.target.value)) setVal(e.target.value);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
          e.target.blur?.();
        }
        if (e.key === "Tab") commit();
        boardTabKeyDown(e);
      }}
      InputProps={{ disableUnderline: true }}
      inputProps={{
        inputMode: "numeric",
        maxLength: 3,
        autoComplete: "off",
        // Hook for FinishTimeBody's auto-focus after a chip click moves a row in.
        "data-aplid": entry.id,
        "data-atf-ord": ord,
      }}
      sx={{
        // Fill the FIXED narrow Pos grid track (set on the card). width:100% +
        // minWidth:0 clip the input's intrinsic size so it never balloons.
        width: "100%",
        minWidth: 0,
        flexShrink: 0,
        touchAction: "manipulation",
        "& input": {
          textAlign: "center", fontWeight: 700, fontFamily: "monospace",
          fontSize: compact768 ? 10 : compactTablet ? 11 : 13,
          p: "2px 2px", bgcolor: "#fff", borderRadius: 2, border: "1px solid #ddd",
          height: 24, boxSizing: "border-box",
          width: "100%", minWidth: 0,
        },
      }}
    />
  );
});

// Pos input for place-mode TYPED boxes — digits only, live-stored in the box
// (the capture consumes it; no per-keystroke value transforms).
const BlankPosInput = React.memo(function BlankPosInput({
  id, value, compact768, compactTablet, onChangeValue, onEnter, ord,
}) {
  return (
    <TextField
      value={value || ""}
      onChange={(e) => {
        if (/^\d*$/.test(e.target.value)) onChangeValue(id, e.target.value);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onEnter(id);
        }
        boardTabKeyDown(e);
      }}
      placeholder="PLC"
      size="small"
      variant="standard"
      InputProps={{ disableUnderline: true }}
      inputProps={{
        inputMode: "numeric",
        maxLength: 3,
        autoComplete: "off",
        "data-atf-ord": ord,
      }}
      sx={{
        // Fill the FIXED narrow Pos grid track (set on the card). width:100% +
        // minWidth:0 clip the input's intrinsic size so it never balloons.
        width: "100%",
        minWidth: 0,
        flexShrink: 0,
        touchAction: "manipulation",
        "& input": {
          textAlign: "center", fontWeight: 700, fontFamily: "monospace",
          fontSize: compact768 ? 10 : compactTablet ? 11 : 13,
          p: "2px 2px", bgcolor: "#fff", borderRadius: 2, border: "1px solid #ddd",
          height: 24, boxSizing: "border-box",
          width: "100%", minWidth: 0,
        },
      }}
    />
  );
});
